import React, { useEffect, useRef, useState } from "react";
import { HiPlus } from "react-icons/hi2";
import { ActivityType, ActivityVariable } from "../../../types/activity";
import { getUnitsForVariable } from "../../../utils/unitUtils";
import {
  formatSecondsToTimeString,
  parseTimeStringToSeconds,
  secondsToUnitValue,
  unitToSeconds,
} from "../../../utils/timeDurationUtils";
import { CreateActivityTypeDialog } from "../../sessions";

export const isTimeVariable = (name: string): boolean =>
  name.toLowerCase() === "time";
export const isRestVariable = (name: string): boolean =>
  name.toLowerCase() === "rest";

/** Target shown under a variable row ("Target 13.10 s"). */
export interface VariableTarget {
  goal: number;
  unit?: string;
}

interface ActivityResultEditorProps {
  activityTypes: ActivityType[];
  loadingActivityTypes: boolean;
  selectedActivity: ActivityType | null;
  variables: ActivityVariable[];
  /** Indices of `variables` the athlete wants to record. */
  selectedIndices: Set<number>;
  onSelectActivity: (activity: ActivityType | null) => void;
  onVariablesChange: (variables: ActivityVariable[]) => void;
  onToggleIndex: (index: number) => void;
  /** Called after a new activity type is created so the parent can reload. */
  onActivityCreated?: (activityTypeId: string) => Promise<void> | void;
  /** Lower-cased variable name → target, when the row is matched to a goal / session target. */
  targets?: Record<string, VariableTarget>;
  /** Hide the per-variable include checkbox (goal editor). */
  hideCheckboxes?: boolean;
  /** Field label used instead of "Activity" when provided. */
  valueLabel?: string;
  /** Edit mode: lock the activity (change via "← Change activity" still allowed). */
  compact?: boolean;
}

/**
 * Activity search + per-variable value/unit rows lifted from
 * AddPerformanceLogForm so the Performance flow (results) and the Goal editor
 * (targets) share one control. Fully controlled: the parent owns the chosen
 * activity and the variable array; this component owns transient UI state
 * (search text, dropdowns, time display strings).
 */
const ActivityResultEditor: React.FC<ActivityResultEditorProps> = ({
  activityTypes,
  loadingActivityTypes,
  selectedActivity,
  variables,
  selectedIndices,
  onSelectActivity,
  onVariablesChange,
  onToggleIndex,
  onActivityCreated,
  targets,
  hideCheckboxes = false,
  compact = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeUnitDropdown, setActiveUnitDropdown] = useState<string | null>(
    null,
  );
  const [timeDisplayValues, setTimeDisplayValues] = useState<
    Record<number, string>
  >({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Seed the time display strings when the variables arrive from outside
  // (edit mode / prefilled rows).
  useEffect(() => {
    const seeded: Record<number, string> = {};
    variables.forEach((v, i) => {
      if (timeDisplayValues[i] !== undefined) return;
      if (isTimeVariable(v.name) && v.measurement !== null) {
        seeded[i] = formatSecondsToTimeString(v.measurement);
      } else if (isRestVariable(v.name) && v.measurement !== null) {
        seeded[i] = formatSecondsToTimeString(
          unitToSeconds(v.measurement, v.unit),
        );
      }
    });
    if (Object.keys(seeded).length > 0) {
      setTimeDisplayValues((prev) => ({ ...seeded, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActivity?.id]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = activityTypes.filter((a) =>
    a.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelect = (activity: ActivityType) => {
    setSearchTerm(activity.name);
    setShowDropdown(false);
    setTimeDisplayValues({});
    onSelectActivity(activity);
  };

  const handleClear = () => {
    setSearchTerm("");
    setTimeDisplayValues({});
    onSelectActivity(null);
  };

  const patchVariable = (index: number, patch: Partial<ActivityVariable>) => {
    const next = [...variables];
    next[index] = { ...next[index], ...patch };
    onVariablesChange(next);
  };

  const handleTimeDisplayChange = (index: number, display: string) => {
    setTimeDisplayValues((prev) => ({ ...prev, [index]: display }));
    patchVariable(index, { measurement: parseTimeStringToSeconds(display) });
  };

  const handleRestDisplayChange = (index: number, display: string) => {
    setTimeDisplayValues((prev) => ({ ...prev, [index]: display }));
    const seconds = parseTimeStringToSeconds(display);
    patchVariable(index, {
      measurement: secondsToUnitValue(seconds, variables[index]?.unit),
    });
  };

  const handleCreated = async (activityTypeId: string) => {
    setIsCreateOpen(false);
    await onActivityCreated?.(activityTypeId);
  };

  return (
    <div>
      {!selectedActivity && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm text-base-content font-sans font-bold">
              Activity
            </h4>
            <span className="text-xs text-base-content/50 font-sans">
              Search activities &amp; packs
            </span>
          </div>
          {loadingActivityTypes ? (
            <div className="text-sm text-base-content/60 font-grotesk">
              Loading activities...
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="relative flex-1" ref={dropdownRef}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={`Search ${activityTypes.length} activities...`}
                  className="w-full px-4 py-3 rounded-md bg-base-200 border border-base-300 focus:border-base-300 focus:outline-none focus:ring-0 text-[16px] font-medium placeholder:text-base-content/40"
                />
                {showDropdown && filtered.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-base-100 border border-base-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
                    {filtered.map((activity) => (
                      <button
                        key={activity.id}
                        type="button"
                        onClick={() => handleSelect(activity)}
                        className="w-full text-left px-3 py-2 hover:bg-base-200 focus:bg-base-200 focus:outline-none"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-base-content font-sans">
                            {activity.name}
                          </span>
                          {activity.isFromPack && (
                            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-base-200 text-base-content/50 font-geist">
                              Pack
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {showDropdown && searchTerm && filtered.length === 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-base-100 border border-base-200 rounded-md shadow-lg p-3 text-sm text-base-content/60 font-sans">
                    No activity types found matching "{searchTerm}"
                  </div>
                )}
              </div>
              {searchTerm && filtered.length === 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setShowDropdown(false);
                    setIsCreateOpen(true);
                  }}
                  className="flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-neutral rounded-md hover:bg-neutral transition-colors font-sans whitespace-nowrap"
                >
                  <HiPlus className="text-sm" />
                  Create
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {selectedActivity && (
        <div>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-base-content/50 hover:text-base-content/80 font-sans mb-2 transition-colors"
          >
            ← Change activity
          </button>
          <div className="mb-1">
            <h4
              className={`${compact ? "text-lg" : "text-2xl"} text-base-content font-sans font-bold`}
            >
              {selectedActivity.name}
            </h4>
          </div>
          <div className="space-y-0.5">
            {variables.map((variable, index) => {
              const checked = hideCheckboxes || selectedIndices.has(index);
              const availableUnits = getUnitsForVariable(variable.name);
              const isTime = isTimeVariable(variable.name);
              const isRest = isRestVariable(variable.name);
              const target = targets?.[variable.name.toLowerCase()];
              return (
                <div key={index}>
                  <div
                    className={`flex items-center gap-3 py-1 transition-opacity ${
                      checked ? "" : "opacity-40"
                    }`}
                  >
                    {!hideCheckboxes && (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggleIndex(index)}
                        className="checkbox checkbox-sm bg-base-100 border-2 border-base-300 checked:bg-neutral checked:border-base-content flex-shrink-0"
                      />
                    )}
                    <span className="text-sm font-sans font-bold text-base-content flex-1 min-w-0 truncate">
                      {variable.name}
                    </span>
                    <div className="relative w-40 flex-shrink-0">
                      <input
                        type={isTime || isRest ? "text" : "number"}
                        disabled={!checked}
                        value={
                          isTime
                            ? (timeDisplayValues[index] ??
                              (variable.measurement !== null
                                ? formatSecondsToTimeString(variable.measurement)
                                : ""))
                            : isRest
                              ? (timeDisplayValues[index] ??
                                (variable.measurement !== null
                                  ? formatSecondsToTimeString(
                                      unitToSeconds(
                                        variable.measurement,
                                        variable.unit,
                                      ),
                                    )
                                  : ""))
                              : (variable.measurement ?? "")
                        }
                        onChange={(e) => {
                          if (isTime) {
                            handleTimeDisplayChange(
                              index,
                              e.target.value.replace(/[^0-9:.]/g, ""),
                            );
                          } else if (isRest) {
                            handleRestDisplayChange(
                              index,
                              e.target.value.replace(/[^0-9:.]/g, ""),
                            );
                          } else {
                            patchVariable(index, {
                              measurement:
                                e.target.value === ""
                                  ? null
                                  : parseFloat(e.target.value),
                            });
                          }
                        }}
                        onBlur={() => {
                          if (isTime) {
                            handleTimeDisplayChange(
                              index,
                              formatSecondsToTimeString(
                                parseTimeStringToSeconds(
                                  timeDisplayValues[index] ?? "",
                                ),
                              ),
                            );
                          } else if (isRest) {
                            handleRestDisplayChange(
                              index,
                              formatSecondsToTimeString(
                                parseTimeStringToSeconds(
                                  timeDisplayValues[index] ?? "",
                                ),
                              ),
                            );
                          }
                        }}
                        placeholder={isTime || isRest ? "0:00.00" : "0"}
                        inputMode="decimal"
                        className="w-full input text-[16px] text-right font-geist tabular-nums sm:text-sm focus:outline-none font-bold focus:ring-0 focus:border-transparent p-3 rounded-md bg-base-200 disabled:cursor-not-allowed"
                        style={{
                          MozAppearance: "textfield",
                          WebkitAppearance: "none",
                          margin: 0,
                          paddingRight: isTime || isRest ? undefined : "80px",
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                      {!isTime && !isRest && (
                        <div
                          className={`absolute right-1 top-1/2 transform -translate-y-1/2 ${
                            activeUnitDropdown === `variable-${index}`
                              ? "z-[100]"
                              : "z-10"
                          }`}
                          style={{ width: "60px" }}
                        >
                          <input
                            type="text"
                            disabled={!checked}
                            value={variable.unit || ""}
                            onChange={(e) =>
                              patchVariable(index, { unit: e.target.value })
                            }
                            onFocus={() =>
                              setActiveUnitDropdown(`variable-${index}`)
                            }
                            onBlur={() =>
                              setTimeout(() => setActiveUnitDropdown(null), 200)
                            }
                            placeholder="unit"
                            className="w-full text-[16px] sm:text-xs px-2 py-1 border border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-300 bg-base-100 font-grotesk text-center disabled:cursor-not-allowed"
                          />
                          {activeUnitDropdown === `variable-${index}` &&
                            availableUnits.length > 0 && (
                              <div className="absolute z-[99999] w-32 mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg max-h-40 overflow-y-auto right-0">
                                {availableUnits.map((unit) => (
                                  <button
                                    key={unit}
                                    type="button"
                                    onClick={() => {
                                      patchVariable(index, { unit });
                                      setActiveUnitDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-base-200 focus:bg-base-200 focus:outline-none font-grotesk"
                                  >
                                    {unit}
                                  </button>
                                ))}
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                  {target && checked && (
                    <p className="text-[11px] text-base-content/50 font-geist tabular-nums text-right pr-1 -mt-0.5 mb-1">
                      Target{" "}
                      {isTime
                        ? formatSecondsToTimeString(target.goal)
                        : `${target.goal}${target.unit ? ` ${target.unit}` : ""}`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CreateActivityTypeDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        initialName={searchTerm}
        onActivityTypeCreated={handleCreated}
      />
    </div>
  );
};

/** Initial variable rows for a freshly selected activity type. */
export function initialVariablesFor(activity: ActivityType): ActivityVariable[] {
  return activity.variables.map((v) => ({
    name: typeof v === "string" ? v : v.name,
    measurement: null,
    unit: typeof v === "string" ? "" : v.defaultUnit || "",
  }));
}

export default ActivityResultEditor;
